import React, { useState } from 'react'
import { Copy, Check } from 'lucide-react'
import { clsx } from 'clsx'
import { log } from '@/utils/logger'

interface CopyButtonProps {
  text: string
  className?: string
  iconSize?: 'sm' | 'md' | 'lg'
  showTooltip?: boolean
  onCopy?: () => void
}

export const CopyButton: React.FC<CopyButtonProps> = ({ 
  text, 
  className = '', 
  iconSize = 'md',
  showTooltip = true,
  onCopy
}) => {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      onCopy?.()
      
      // Reset after 2 seconds
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      log.error('Failed to copy to clipboard:', error)
      
      // Fallback for older browsers
      try {
        const textArea = document.createElement('textarea')
        textArea.value = text
        textArea.style.position = 'fixed'
        textArea.style.opacity = '0'
        document.body.appendChild(textArea)
        textArea.focus()
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)
        
        setCopied(true)
        onCopy?.()
        setTimeout(() => setCopied(false), 2000)
      } catch (fallbackError) {
        log.error('Fallback copy method also failed:', fallbackError)
      }
    }
  }

  const iconSizeClasses = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5'
  }

  return (
    <div className="relative inline-block">
      <button
        onClick={handleCopy}
        className={clsx(
          'inline-flex items-center justify-center transition-colors duration-200',
          'hover:bg-gray-100 dark:hover:bg-gray-800 rounded p-1',
          copied 
            ? 'text-green-600 dark:text-green-400' 
            : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200',
          className
        )}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? (
          <Check className={iconSizeClasses[iconSize]} />
        ) : (
          <Copy className={iconSizeClasses[iconSize]} />
        )}
      </button>
      
      {/* Tooltip */}
      {showTooltip && copied && (
        <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 z-50 transition-all duration-200 scale-100 opacity-100">
          <div className="bg-gray-900 dark:bg-gray-700 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap">
            Copied!
          </div>
        </div>
      )}
    </div>
  )
}

interface CopyTextProps {
  text: string
  displayText?: string
  className?: string
  copyButtonClass?: string
  showFullText?: boolean
  truncateLength?: number
}

export const CopyText: React.FC<CopyTextProps> = ({
  text,
  displayText,
  className = '',
  copyButtonClass = '',
  showFullText = false,
  truncateLength = 8
}) => {
  const display = displayText || (
    showFullText 
      ? text 
      : text.length > truncateLength * 2 + 3
        ? `${text.slice(0, truncateLength)}...${text.slice(-truncateLength)}`
        : text
  )

  return (
    <div className={clsx('inline-flex items-center space-x-2', className)}>
      <span className="font-mono text-sm">{display}</span>
      <CopyButton 
        text={text} 
        className={copyButtonClass}
        iconSize="sm"
      />
    </div>
  )
}